import { HttpClient } from '@angular/common/http';
import { PLATFORM_ID, inject, provideAppInitializer } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ENVIRONMENT } from '@vendorhub/shared/config';
import { RefreshResponseSchema, TokenStore } from '@vendorhub/shared/data-access';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from './auth.store';

/**
 * Boot-time auth initialization.
 *
 * Flow:
 *  1. SSR → mark store initialized, skip everything else (no cookies, no
 *     localStorage on the server).
 *  2. Browser with a stored refresh token → attempt a silent refresh. On
 *     success, fetch `/me`. On failure, clear the session and mark
 *     initialized.
 *  3. Browser without a stored refresh token → just mark initialized.
 *
 * The silent refresh uses `HttpClient` directly; the auth interceptor
 * won't attach a Bearer token (we don't have one yet), and the error
 * interceptor skips `/identity/refresh` to avoid recursion.
 */
export function provideAuthInitializer() {
  return provideAppInitializer(async () => {
    const platformId = inject(PLATFORM_ID);
    const auth = inject(AuthStore);
    const tokens = inject(TokenStore);

    if (!isPlatformBrowser(platformId)) {
      auth.clearSession();
      return;
    }

    if (!tokens.refreshToken()) {
      auth.clearSession();
      return;
    }

    const http = inject(HttpClient);
    const env = inject(ENVIRONMENT);

    try {
      const raw = await firstValueFrom(
        http.post<unknown>(
          `${env.apiBaseUrl}/api/v1/identity/refresh`,
          { refreshToken: tokens.refreshToken() },
          { withCredentials: true },
        ),
      );
      const parsed = RefreshResponseSchema.safeParse(raw);
      if (!parsed.success) {
        auth.clearSession();
        return;
      }
      tokens.set(
        parsed.data.data.accessToken,
        parsed.data.data.expiresIn,
        parsed.data.data.refreshToken,
      );
      auth.hydrate();
    } catch {
      auth.clearSession();
    }
  });
}
